import { GetServerSidePropsContext } from 'next'
import { z } from 'zod'
import { getExperimentVariables } from '@mc/next-growthbook'
import { reportError } from '@mc/next-newrelic'

// https://app.growthbook.io/features/mccerts-2664-cert-page-copy
export const EXPERIMENT_FLAG = 'mccerts-2664-cert-page-copy'

const Mccerts2664DataSchema = z.object({
  eyebrow: z.string().optional(),
  headline: z.string().optional(),
  subheadline: z.string().optional(),
  outcomes: z.array(z.string()).optional(),
  images: z
    .object({
      hero1x1: z.object({
        url: z.string(),
        altText: z.string().optional(),
      }),
    })
    .optional(),
})

// `certificates` is optional so a user who isn't in the experiment (empty
// variables) still validates and resolves to null.
const Mccerts2664VariablesSchema = z.object({
  certificates: z.record(z.string(), Mccerts2664DataSchema).optional(),
})

export type Mccerts2664Data = z.infer<typeof Mccerts2664DataSchema>
export type Mccerts2664Variables = z.infer<typeof Mccerts2664VariablesSchema>

/**
 * Resolve the mccerts-2664-cert-page-copy variant payload for a given cert.
 *
 * Reads the experiment variables DIRECTLY (not via getGrowthBookVariables, which
 * drops the control bucket). That matters here: a configured cert must resolve a
 * non-null payload for BOTH arms — an empty `{}` for control, the real copy for
 * variant_1 — so exposure can fire equally for both arms and only on certs that
 * are actually configured in the GrowthBook variables. Certs not listed under
 * `certificates` (and users outside the experiment) resolve to null → no
 * override and no exposure.
 */
export const getMccerts2664Variables = async (
  ctx: GetServerSidePropsContext,
  certSlug: string,
  flag: string = EXPERIMENT_FLAG,
): Promise<Mccerts2664Data | null> => {
  const variables = await getExperimentVariables(ctx, flag)

  if (!variables) {
    return null
  }

  const validated = Mccerts2664VariablesSchema.safeParse(variables)

  if (!validated.success) {
    reportError(validated.error, {
      message: `Invalid ${flag} variables format`,
      flag,
      variables: JSON.stringify(variables),
    })
    return null
  }

  // Empty `{}` for a configured cert in control; the copy payload in variant_1.
  return validated.data.certificates?.[certSlug] ?? null
}
